import { TransText } from '@/i18n/trans/text';
import { breakpoints } from '@/utils/breakpoints';
import { Button, Popover, Sheet } from '@packages/webshop-design-system';
import { type FC, type ReactNode } from 'react';
import { useMediaQuery } from 'react-responsive';

export type RefundOptionType = 'trips' | 'extras' | 'travelPasses';

interface RefundOption {
  type: RefundOptionType;
  labelKey:
    | 'refundTripsOption'
    | 'refundExtrasOption'
    | 'refundTravelPassesOption';
  icon: ReactNode;
}

interface RefundOptionsProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  trigger?: ReactNode;
  options: Array<RefundOption>;
  onOptionClick: (type: RefundOptionType) => void;
}

const RefundOptions: FC<RefundOptionsProps> = ({
  isOpen,
  onOpenChange,
  trigger,
  options,
  onOptionClick,
}) => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const handleOptionClick = (type: RefundOptionType) => {
    onOptionClick(type);
    onOpenChange(false);
  };

  const menuContent = (
    <div className="flex flex-col gap-2">
      {options.map(({ type, labelKey, icon }) => (
        <Button
          key={type}
          variant="secondary"
          onClick={() => handleOptionClick(type)}
          className="justify-center"
          data-testid={`refund-option-${type}`}
        >
          {icon}
          <TransText i18nKey={labelKey} />
        </Button>
      ))}
    </div>
  );

  if (isLaptopOrBigger) {
    return (
      <Popover
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        trigger={trigger}
        data-testid="refund-options-popover"
        placement="top"
        content={menuContent}
        className={{ root: 'w-64' }}
      />
    );
  }

  return (
    <Sheet
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      trigger={trigger}
      side="bottom"
      headerTitle={<TransText i18nKey="refund" />}
      className={{ root: 'min-w-full' }}
      data-testid="refund-options-sheet"
    >
      {menuContent}
    </Sheet>
  );
};

export default RefundOptions;
